import * as React from 'react'
import Select from 'react-select'
import { Button, Modal, Input } from 'src/design-system'
import { SkillsService } from 'app/packs/src/services/skills-service'
import { useApiClient } from 'app/packs/src/utils/use-api-client'
import { useRailsContext } from 'app/packs/src/components/rails-context'
import { successToast } from 'app/packs/src/utils/success-toast'
import { errorToast } from 'app/packs/src/utils/error-toast'
import { store } from 'store/index'
import { useContext } from 'react'
import { BulkDeletedSkillContext } from 'pages/org-skills-page/deleted-skill-context'

interface SkillConvertVariantDialogProps {
  defaultOpen: boolean
  onOpenChange: (open: boolean) => void
  skillId: string
  source?: string
  variantCount?: number
  onSkillConverted?: (skillId: string) => void
}

export const SkillConvertVariantDialog: React.FC<
  SkillConvertVariantDialogProps
> = ({
  defaultOpen,
  onOpenChange,
  skillId,
  onSkillConverted,
  variantCount = 0,
}) => {
  const [selectedSkillId, setSelectedSkillId] = React.useState<string | null>(
    null
  )
  const [variantName, setVariantName] = React.useState('')
  const [loading, setLoading] = React.useState(false)

  const client = useApiClient()
  const { org } = useRailsContext()
  const service = React.useMemo(
    () => new SkillsService(client, org ?? undefined),
    [client, org]
  )
  const storeSkills = store.skills
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const allskills = Array.from(storeSkills.data.values()).map((skill) => ({
    id: skill.id,
    name: skill.name,
  }))

  const { removeSkillFromData } = useContext(BulkDeletedSkillContext)

  const allSkills = React.useMemo(
    () =>
      Array.from(store.skills.data.values())
        .filter((skill) => skill.id !== skillId)
        .map(({ id, name }) => ({
          value: id,
          label: name,
        })),
    [skillId]
  )

  const handleConvert = async () => {
    if (!selectedSkillId || !variantName || variantCount > 1) return
    setLoading(true)
    try {
      const response = await service.convertToVariant({
        skill_id: skillId,
        selected_skill_id: selectedSkillId,
        variant_name: variantName,
      })
      if (response?.success) {
        store.skills.fetchAll()
        removeSkillFromData?.(skillId)
        onSkillConverted?.(skillId)
        successToast('Skill successfully converted to variant.')
        onOpenChange(false)
      } else {
        errorToast('Failed to convert skill.')
      }
    } catch {
      errorToast('An error occurred while converting the skill.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal.Root
      open={defaultOpen}
      onOpenChange={onOpenChange}
      title="Convert to Variant"
    >
      <div className="p-6 w-full max-w-2xl h-[350px] bg-white rounded-xl shadow-lg">
        <h1 className="text-xl">Convert to variant of...</h1>
        {variantCount > 1 ? (
          <div className="mt-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-md">
            This skill already has multiple variants ({variantCount}). You
            cannot convert it into another variant.
          </div>
        ) : (
          <p className="text-gray-600 mt-2">
            This will convert this skill into a variant of another skill. It
            will remain in any teams and positions it is currently used in.
          </p>
        )}

        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-semibold">Skill</label>
            <Select
              options={allSkills}
              value={allSkills.find((s) => s.value === selectedSkillId) || null}
              onChange={(selected) =>
                setSelectedSkillId(selected?.value || null)
              }
              isDisabled={loading || variantCount > 1}
              classNamePrefix="react-select"
              placeholder="Choose a skill to create a variant of"
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 100 }) }}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold">Variant Name</label>
            <Input
              type="text"
              placeholder="E.g., Engineering"
              value={variantName}
              onChange={(e) => setVariantName(e.target.value)}
              disabled={loading || variantCount > 1}
              className="w-full mt-1 rounded-md px-3 py-2 text-sm focus:ring-green-500 focus:border-green-500"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleConvert}
            disabled={
              loading || !selectedSkillId || !variantName || variantCount > 1
            }
          >
            Convert to variant
          </Button>
        </div>
      </div>
    </Modal.Root>
  )
}
